import axios from "axios";
import { SESSION_EXPIRED_EVENT, clearClientAuthData } from "../Components/Assets/AppConstant";

/**
 * Base axios instance
 */
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  withCredentials: true, // 🔥 REQUIRED FOR HTTP-ONLY COOKIES
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60000,
});

/**
 * REQUEST INTERCEPTOR
 * (Client key, custom headers, etc.)
 */
apiClient.interceptors.request.use(
  (config) => {
    // Public client key (NOT secret)
    const clientKey = process.env.REACT_APP_CLIENT_KEY;
    if (clientKey) {
      config.headers["x-client-key"] = clientKey;
    }

    return config;
  },
  //(error) => Promise.reject(error)
  (error) => {
    const isAuthenticated = localStorage.getItem("IS_AUTHENTICATED") === "true";
    const status = error?.response?.status;
    if ([401, 419, 440].includes(status) && isAuthenticated) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      clearClientAuthData();
    }

    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Centralized error format
    const isAuthenticated = localStorage.getItem("IS_AUTHENTICATED") === "true";
    const status = error?.response?.status;
    if ([401, 419, 440].includes(status) && isAuthenticated) {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      clearClientAuthData();
    }

    if (error.response) {
      return Promise.reject({
        status: error.response.status,
        data: error.response.data,
      });
    }

    return Promise.reject({
      status: 500,
      message: "Network error",
    });
  }
);

/**
 * 🔹 SINGLE API CALL
 */
export const callSingleApi = async ({
  url,
  method = "GET",
  data = {},
  params = {},
  headers = {},
}) => {
  try {
    const response = await apiClient({
      url,
      method,
      data,
      params,
      headers,
    });

    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * 🔹 MULTIPLE API CALLS (PARALLEL)
 */
export const callMultipleApis = async (apiList = []) => {
  try {
    const requests = apiList.map((api) =>
      apiClient({
        url: api.url,
        method: api.method || "GET",
        data: api.data || {},
        params: api.params || {},
        headers: api.headers || {},
      })
    );

    const responses = await Promise.all(requests);
    return responses.map((res) => res.data);
  } catch (error) {
    throw error;
  }
};

export default apiClient;
